import { base64UrlDecode } from "./encoding.js";
import {
  type DevicePublicKeys,
  type IssuerSigningKeyPair,
  type P256PublicJwk,
  type RelationshipPermission,
  importP256VerificationKey,
  issuerPublicKeyId,
  signEs256,
  validateDevicePublicKeys,
  verifyEs256,
} from "./keys.js";
import {
  asRecord,
  assertExactKeys,
  assertHttpsOrigin,
  assertLiteral,
  assertSafeIdentifier,
  fail,
  readArray,
  readInteger,
  readString,
} from "./validation.js";

export const RELATIONSHIP_CARD_VERSION = 1 as const;
export type RelationshipCardState = "pending" | "current" | "revoked";
export type RelationshipRole = "owner" | "participant";

export interface RelationshipCardDevice {
  readonly partyId: string;
  readonly role: RelationshipRole;
  readonly evidence: string;
  readonly keys: DevicePublicKeys;
}

export interface RelationshipCardPayload {
  readonly version: typeof RELATIONSHIP_CARD_VERSION;
  readonly issuer: string;
  readonly audience: string;
  readonly operatorId: string;
  readonly operatorOrganizationId: string;
  readonly relyingOrganizationId: string;
  readonly operatorIndependent: true;
  readonly relationshipId: string;
  readonly sequence: number;
  readonly previousCardHash: string | null;
  readonly state: RelationshipCardState;
  readonly purpose: string;
  readonly scope: string;
  readonly permissions: readonly RelationshipPermission[];
  readonly issuedAtMs: number;
  readonly notBeforeMs: number;
  readonly expiresAtMs: number;
  readonly devices: readonly RelationshipCardDevice[];
}

export interface SignedRelationshipCard {
  readonly version: typeof RELATIONSHIP_CARD_VERSION;
  readonly issuerKeyId: string;
  readonly payload: RelationshipCardPayload;
  readonly signature: string;
}

export interface RelationshipCardVerification {
  readonly issuerPublicKey: P256PublicJwk;
  readonly expectedIssuer: string;
  readonly expectedAudience: string;
  readonly expectedOperatorId: string;
  readonly expectedOperatorOrganizationId: string;
  readonly expectedRelyingOrganizationId: string;
  readonly expectedRelationshipId: string;
  readonly expectedSequence: number;
  readonly expectedPreviousCardHash: string | null;
  readonly nowMs: number;
}

export async function signRelationshipCard(
  payloadInput: RelationshipCardPayload,
  issuer: IssuerSigningKeyPair,
): Promise<SignedRelationshipCard> {
  const payload = await normalizeRelationshipCardPayload(payloadInput);
  if (issuer.issuerKeyId !== (await issuerPublicKeyId(issuer.publicKey))) {
    return fail("ISSUER_KEY_MISMATCH", "Issuer key identifier is invalid");
  }
  const protectedCard = {
    version: RELATIONSHIP_CARD_VERSION,
    issuerKeyId: issuer.issuerKeyId,
    payload,
  } as const;
  return {
    ...protectedCard,
    signature: await signEs256(
      issuer.keyPair.privateKey,
      "relationship-card",
      protectedCard,
    ),
  };
}

export async function verifyRelationshipCard(
  cardInput: SignedRelationshipCard,
  requirements: RelationshipCardVerification,
): Promise<RelationshipCardPayload> {
  validateVerificationRequirements(requirements);
  const cardRecord = asRecord(cardInput, "signed relationship card");
  assertExactKeys(
    cardRecord,
    ["version", "issuerKeyId", "payload", "signature"],
    "signed relationship card",
  );
  assertLiteral(cardRecord.version, RELATIONSHIP_CARD_VERSION, "signed relationship card.version");
  const issuerKeyId = readString(cardRecord, "issuerKeyId", "signed relationship card", 64);
  const signature = readString(cardRecord, "signature", "signed relationship card", 128);
  if (base64UrlDecode(issuerKeyId, 32).byteLength !== 32) {
    return fail("INVALID_CARD", "Issuer key identifier is invalid");
  }
  const payload = await normalizeRelationshipCardPayload(cardRecord.payload);
  const expectedIssuerKeyId = await issuerPublicKeyId(requirements.issuerPublicKey);
  if (issuerKeyId !== expectedIssuerKeyId) {
    return fail("UNTRUSTED_ISSUER", "Relationship card issuer key is not trusted");
  }
  const issuerVerificationKey = await importP256VerificationKey(requirements.issuerPublicKey);
  if (
    !(await verifyEs256(
      issuerVerificationKey,
      "relationship-card",
      { version: RELATIONSHIP_CARD_VERSION, issuerKeyId, payload },
      signature,
    ))
  ) {
    return fail("INVALID_CARD_SIGNATURE", "Relationship card signature is invalid");
  }

  if (payload.issuer !== requirements.expectedIssuer) {
    return fail("WRONG_ISSUER", "Relationship card issuer does not match");
  }
  if (payload.audience !== requirements.expectedAudience) {
    return fail("WRONG_AUDIENCE", "Relationship card audience does not match");
  }
  if (payload.operatorId !== requirements.expectedOperatorId) {
    return fail("WRONG_OPERATOR", "Relationship card operator does not match");
  }
  if (
    payload.operatorOrganizationId !==
    requirements.expectedOperatorOrganizationId
  ) {
    return fail(
      "WRONG_OPERATOR_ORGANIZATION",
      "Relationship card operator organization does not match",
    );
  }
  if (
    payload.relyingOrganizationId !== requirements.expectedRelyingOrganizationId
  ) {
    return fail(
      "WRONG_RELYING_ORGANIZATION",
      "Relationship card relying organization does not match",
    );
  }
  if (payload.relationshipId !== requirements.expectedRelationshipId) {
    return fail("WRONG_RELATIONSHIP", "Relationship card relationship does not match");
  }
  if (payload.sequence !== requirements.expectedSequence) {
    return fail("WRONG_CARD_SEQUENCE", "Relationship card sequence does not match");
  }
  if (payload.previousCardHash !== requirements.expectedPreviousCardHash) {
    return fail("WRONG_CARD_CHAIN", "Relationship card does not extend the expected revision");
  }
  if (payload.state !== "current") {
    return fail("CARD_NOT_CURRENT", "Relationship card is not current");
  }
  if (
    requirements.nowMs < payload.notBeforeMs ||
    requirements.nowMs >= payload.expiresAtMs ||
    requirements.nowMs < payload.issuedAtMs
  ) {
    return fail("CARD_NOT_CURRENT", "Relationship card validity window is not current");
  }

  return payload;
}

export async function normalizeRelationshipCardPayload(
  value: unknown,
): Promise<RelationshipCardPayload> {
  const record = asRecord(value, "relationship card payload");
  assertExactKeys(
    record,
    [
      "version",
      "issuer",
      "audience",
      "operatorId",
      "operatorOrganizationId",
      "relyingOrganizationId",
      "operatorIndependent",
      "relationshipId",
      "sequence",
      "previousCardHash",
      "state",
      "purpose",
      "scope",
      "permissions",
      "issuedAtMs",
      "notBeforeMs",
      "expiresAtMs",
      "devices",
    ],
    "relationship card payload",
  );
  assertLiteral(record.version, RELATIONSHIP_CARD_VERSION, "relationship card payload.version");
  const issuer = readString(record, "issuer", "relationship card payload", 512);
  const audience = readString(record, "audience", "relationship card payload", 512);
  const operatorId = readString(record, "operatorId", "relationship card payload", 256);
  const operatorOrganizationId = readString(
    record,
    "operatorOrganizationId",
    "relationship card payload",
    256,
  );
  const relyingOrganizationId = readString(
    record,
    "relyingOrganizationId",
    "relationship card payload",
    256,
  );
  assertLiteral(
    record.operatorIndependent,
    true,
    "relationship card payload.operatorIndependent",
  );
  const relationshipId = readString(record, "relationshipId", "relationship card payload", 256);
  const sequence = readInteger(record, "sequence", "relationship card payload", 1);
  const state = readString(record, "state", "relationship card payload", 16);
  const purpose = readString(record, "purpose", "relationship card payload", 1024);
  const scope = readString(record, "scope", "relationship card payload", 256);
  const permissions = normalizeRelationshipPermissions(
    readArray(record, "permissions", "relationship card payload", 2),
  );
  const issuedAtMs = readInteger(record, "issuedAtMs", "relationship card payload", 0);
  const notBeforeMs = readInteger(record, "notBeforeMs", "relationship card payload", 0);
  const expiresAtMs = readInteger(record, "expiresAtMs", "relationship card payload", 1);
  assertHttpsOrigin(issuer, "relationship card payload.issuer");
  assertHttpsOrigin(audience, "relationship card payload.audience");
  assertSafeIdentifier(operatorId, "relationship card payload.operatorId");
  assertSafeIdentifier(
    operatorOrganizationId,
    "relationship card payload.operatorOrganizationId",
  );
  assertSafeIdentifier(
    relyingOrganizationId,
    "relationship card payload.relyingOrganizationId",
  );
  assertSafeIdentifier(relationshipId, "relationship card payload.relationshipId");
  if (operatorOrganizationId === relyingOrganizationId) {
    return fail(
      "OPERATOR_NOT_INDEPENDENT",
      "Operator and relying organization identifiers must be distinct",
    );
  }
  let previousCardHash: string | null;
  if (record.previousCardHash === null) {
    previousCardHash = null;
  } else if (typeof record.previousCardHash === "string") {
    previousCardHash = record.previousCardHash;
    if (base64UrlDecode(previousCardHash, 32).byteLength !== 32) {
      return fail("INVALID_CARD", "Previous card hash must be a 32-byte digest");
    }
  } else {
    return fail("INVALID_CARD", "Previous card hash is invalid");
  }
  if (
    (sequence === 1 && previousCardHash !== null) ||
    (sequence > 1 && previousCardHash === null)
  ) {
    return fail("INVALID_CARD", "Previous card hash does not match the card sequence");
  }
  if (state !== "pending" && state !== "current" && state !== "revoked") {
    return fail("INVALID_CARD", "Relationship card state is invalid");
  }
  if (
    issuedAtMs > notBeforeMs ||
    issuedAtMs >= expiresAtMs ||
    notBeforeMs >= expiresAtMs
  ) {
    return fail("INVALID_CARD", "Relationship card validity window is invalid");
  }

  const deviceInputs = readArray(record, "devices", "relationship card payload", 100);
  const devices: RelationshipCardDevice[] = [];
  const deviceKeyIds = new Set<string>();
  let ownerCount = 0;
  for (const [index, deviceInput] of deviceInputs.entries()) {
    const deviceRecord = asRecord(deviceInput, `relationship card device ${index}`);
    assertExactKeys(
      deviceRecord,
      ["partyId", "role", "evidence", "keys"],
      `relationship card device ${index}`,
    );
    const partyId = readString(deviceRecord, "partyId", `relationship card device ${index}`, 256);
    const role = readString(deviceRecord, "role", `relationship card device ${index}`, 32);
    const evidence = readString(deviceRecord, "evidence", `relationship card device ${index}`, 512);
    assertSafeIdentifier(partyId, `relationship card device ${index}.partyId`);
    if (role !== "owner" && role !== "participant") {
      return fail("INVALID_CARD", "Relationship card device role is invalid");
    }
    if (role === "owner") {
      ownerCount += 1;
    }
    const keys = await validateDevicePublicKeys(deviceRecord.keys);
    if (deviceKeyIds.has(keys.deviceKeyId)) {
      return fail("INVALID_CARD", "Relationship card contains a duplicate device key");
    }
    deviceKeyIds.add(keys.deviceKeyId);
    devices.push({ partyId, role, evidence, keys });
  }
  if (state !== "pending" && ownerCount === 0) {
    return fail("INVALID_CARD", "Relationship card must contain an owner device");
  }
  devices.sort((left, right) => compareCodeUnits(left.keys.deviceKeyId, right.keys.deviceKeyId));

  return {
    version: RELATIONSHIP_CARD_VERSION,
    issuer,
    audience,
    operatorId,
    operatorOrganizationId,
    relyingOrganizationId,
    operatorIndependent: true,
    relationshipId,
    sequence,
    previousCardHash,
    state,
    purpose,
    scope,
    permissions,
    issuedAtMs,
    notBeforeMs,
    expiresAtMs,
    devices,
  };
}

function normalizeRelationshipPermissions(
  values: readonly unknown[],
): readonly RelationshipPermission[] {
  if (values.length === 0) {
    return fail("INVALID_CARD", "Relationship card permissions must not be empty");
  }
  const permissions = new Set<RelationshipPermission>();
  for (const value of values) {
    if (value !== "open" && value !== "lock") {
      return fail("INVALID_CARD", "Relationship card permission is invalid");
    }
    if (permissions.has(value)) {
      return fail("INVALID_CARD", "Relationship card permission is duplicated");
    }
    permissions.add(value);
  }
  return (["open", "lock"] as const).filter((permission) => permissions.has(permission));
}

function compareCodeUnits(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function validateVerificationRequirements(requirements: RelationshipCardVerification): void {
  assertHttpsOrigin(requirements.expectedIssuer, "expected issuer");
  assertHttpsOrigin(requirements.expectedAudience, "expected audience");
  assertSafeIdentifier(requirements.expectedOperatorId, "expected operator id");
  assertSafeIdentifier(
    requirements.expectedOperatorOrganizationId,
    "expected operator organization id",
  );
  assertSafeIdentifier(
    requirements.expectedRelyingOrganizationId,
    "expected relying organization id",
  );
  assertSafeIdentifier(requirements.expectedRelationshipId, "expected relationship id");
  if (
    !Number.isSafeInteger(requirements.expectedSequence) ||
    requirements.expectedSequence < 1 ||
    !Number.isSafeInteger(requirements.nowMs) ||
    requirements.nowMs < 0
  ) {
    fail("INVALID_REQUIREMENT", "Card verification requirements are invalid");
  }
  if (
    requirements.expectedOperatorOrganizationId ===
    requirements.expectedRelyingOrganizationId
  ) {
    fail("INVALID_REQUIREMENT", "Expected operator must be independent");
  }
  if (requirements.expectedPreviousCardHash === null) {
    if (requirements.expectedSequence !== 1) {
      fail("INVALID_REQUIREMENT", "Expected card chain is missing its previous hash");
    }
  } else if (
    base64UrlDecode(requirements.expectedPreviousCardHash, 32).byteLength !== 32 ||
    requirements.expectedSequence === 1
  ) {
    fail("INVALID_REQUIREMENT", "Expected previous card hash is invalid");
  }
}
